import React, { useState, useEffect, useRef } from 'react';
import { formatDate } from '../utils/dateUtils';
import { useAuth } from '../context/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { coins, images, transactions, certLookup } from '../utils/api';
import { fetchCertImages } from '../utils/certImages';
import EditCoinForm from '../components/EditCoinForm';
import SaleTransactionModal from '../components/SaleTransactionModal';
import ImageManager from '../components/ViewCoin/ImageManager';
import ImageViewerModal from '../components/shared/ImageViewerModal';
import Header from '../components/ViewCoin/Header';
import BasicInfo from '../components/ViewCoin/BasicInfo';
import CoinDetails from '../components/ViewCoin/CoinDetails';
import PopulationInfo from '../components/ViewCoin/PopulationInfo';
import PurchaseInfo from '../components/ViewCoin/PurchaseInfo';
import GradingHistory from '../components/ViewCoin/GradingHistory';
import Notes from '../components/ViewCoin/Notes';
import Provenance from '../components/ViewCoin/Provenance';
import CoinDetailsText from '../components/ViewCoin/CoinDetailsText';
import CustomFieldsSection from '../components/ViewCoin/CustomFieldsSection';
import DocumentsSection from '../components/ViewCoin/DocumentsSection';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import AuctionImagesModal from '../components/ViewCoin/AuctionImagesModal';

const ViewCoin = () => {
  // Track if component is mounted
  const isMounted = useRef(true);
  
  // Update mounted status on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);
  const { id } = useParams();
  const navigate = useNavigate();
  const [coin, setCoin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingTransaction, setLoadingTransaction] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [submissionHistory, setSubmissionHistory] = useState([]);
  const [purchaseTransaction, setPurchaseTransaction] = useState(null);
  const [adjacentIds, setAdjacentIds] = useState({ previousId: null, nextId: null });
  const [isAuctionModalOpen, setIsAuctionModalOpen] = useState(false);
  const [auctionImages, setAuctionImages] = useState([]);
  const [filters] = useState(() => {
    // Get filters from URL or sessionStorage
    const searchParams = new URLSearchParams(window.location.search);
    const storedFilters = sessionStorage.getItem('inventoryFilters');
    return storedFilters ? JSON.parse(storedFilters) : Object.fromEntries(searchParams);
  });

  const handleImageClick = (images, index) => {
    setSelectedImage({ images, index, coin });
  };

  const closeImageModal = () => {
    setSelectedImage(null);
  };

  const handleNavigate = (newIndex) => {
    setSelectedImage(prev => ({ ...prev, index: newIndex }));
  };
  const [loadingSubmissions, setLoadingSubmissions] = useState(true);
  const fileInputRef = useRef();
  const [isUploading, setIsUploading] = useState(false);
  const [isFetchingImages, setIsFetchingImages] = useState(false);
  const { user } = useAuth();

  const handleEdit = () => {
    setIsEditing(true);
  };

  const fetchCoinData = async () => {
    try {
      const data = await coins.getById(id);
      setCoin(data);
    } catch (err) {
      console.error('Failed to fetch coin:', err);
      setError('Failed to load coin details. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAdjacentIds = async () => {
    try {
      const { previousId, nextId } = await coins.getAdjacentIds(id, filters);
      setAdjacentIds({ previousId, nextId });
    } catch (err) {
      console.error('Failed to fetch adjacent coin IDs:', err);
    }
  };

  const handlePrevious = () => {
    if (adjacentIds.previousId) {
      navigate(`/coins/${adjacentIds.previousId}${window.location.search}`);
    }
  };

  const handleNext = () => {
    if (adjacentIds.nextId) {
      navigate(`/coins/${adjacentIds.nextId}${window.location.search}`);
    }
  };

  useEffect(() => {
    const fetchSubmissionHistory = async () => {
      try {
        const data = await coins.getSubmissionHistory(id);
        setSubmissionHistory(data);
      } catch (err) {
        console.error('Failed to fetch submission history:', err);
      } finally {
        setLoadingSubmissions(false);
      }
    };

    const fetchData = async () => {
      try {
        setLoading(true);
        setLoadingTransaction(true);

        // Fetch coin data and adjacent IDs in parallel
        const [coinData, adjacentData] = await Promise.all([
          coins.getById(id),
          coins.getAdjacentIds(id, filters)
        ]);
        setCoin(coinData);
        setAdjacentIds(adjacentData);

        // Fetch purchase transaction if coin has one
        if (coinData.purchase_transaction_id) {
          const transactionDetails = await transactions.getById(coinData.purchase_transaction_id);
          setPurchaseTransaction(transactionDetails);
        } else {
          setPurchaseTransaction(null);
        }

        // Fetch submission history
        await fetchSubmissionHistory();

        setLoading(false);
        setLoadingTransaction(false);
      } catch (err) {
        console.error('Failed to fetch data:', err);
        setError('Failed to load coin details. Please try again later.');
        setLoading(false);
        setLoadingTransaction(false);
      }
    };

    if (isMounted.current) {
      fetchData();
    }
  }, [id]);

  // Listen for tag changes
  useEffect(() => {
    const handleTagChange = () => {
      fetchCoinData();
    };

    window.addEventListener('tagChange', handleTagChange);
    return () => window.removeEventListener('tagChange', handleTagChange);
  }, []);

  const handleSubmit = async (data) => {
    try {
      setLoading(true);
      setLoadingTransaction(true);
      
      // Update the coin and wait for it to complete
      const updatedCoin = await coins.update(id, data);
      
      // Then fetch all related data in parallel
      const [submissionData, transactionDetails] = await Promise.all([
        coins.getSubmissionHistory(id),
        updatedCoin.purchase_transaction_id 
          ? transactions.getById(updatedCoin.purchase_transaction_id)
          : Promise.resolve(null)
      ]);

      // Update all state at once
      setCoin(updatedCoin);
      setSubmissionHistory(submissionData);
      setPurchaseTransaction(transactionDetails);

      setLoading(false);
      setLoadingTransaction(false);
      setIsEditing(false);
    } catch (err) {
      console.error('Failed to update coin:', err);
      setError('Failed to update coin. Please try again.');
      setLoading(false);
      setLoadingTransaction(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await coins.delete(id);
      navigate('/inventory');
    } catch (err) {
      console.error('Failed to delete coin:', err);
      if (err.message.includes('transaction')) {
        setError('This coin is part of a transaction and cannot be deleted. Please delete the transaction first.');
      } else {
        setError('Failed to delete coin. Please try again.');
      }
    }
    setIsDeleteModalOpen(false);
  };

  const handleAuctionImagesConfirm = async () => {
    try {
      const copiedImages = await images.copyAuctionImages(coin.id, auctionImages);
      setCoin(prev => ({
        ...prev,
        images: [...prev.images, ...copiedImages]
      }));
    } catch (err) {
      console.error('Failed to copy auction images:', err);
      alert('Failed to copy auction images. Please try again.');
    } finally {
      setIsAuctionModalOpen(false);
      setAuctionImages([]);
    }
  };

  if (loading || loadingTransaction) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4 my-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm font-medium text-red-800">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!coin) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Coin not found.</p>
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className="container mx-auto px-2 pt-4">
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : (
          <EditCoinForm 
            coinId={id}
            onSubmit={handleSubmit} 
            onCancel={() => setIsEditing(false)} 
          />
        )}
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-4">
      <Header 
        onBack={() => navigate('/inventory')}
        onEdit={handleEdit}
        onSell={() => setIsTransactionModalOpen(true)}
        onDelete={handleDelete}
        isSold={coin.status_name === 'Sold'}
        onUploadClick={() => fileInputRef.current?.click()}
        isUploading={isUploading}
        imageCount={coin.images?.length || 0}
        hasCertNumber={!!coin.certification_service_name && !!coin.certification_number}
        isFetchingImages={isFetchingImages}
        onPrevious={handlePrevious}
        onNext={handleNext}
        hasPrevious={!!adjacentIds.previousId}
        hasNext={!!adjacentIds.nextId}
        onFetchImages={async () => {
          try {
            setIsFetchingImages(true);
            // Get cert images
            const certImages = await fetchCertImages(
              coin.certification_number,
              coin.certification_service_name,
              coin.grade
            );
            
            let updatedCoin = coin;
            if (certImages.length > 0) {
              // Update coin with cert images
              updatedCoin = await coins.update(coin.id, {
                images: certImages,
                certification_number: coin.certification_number
              });
              // Update state with new coin data
              setCoin(updatedCoin);
            }
            
            // Check for auction house images regardless of cert images
            const foundAuctionImages = await images.checkAuctionImages(
              coin.certification_service_name,
              coin.certification_number,
              coin.id
            );

            if (foundAuctionImages && foundAuctionImages.length > 0) {
              setAuctionImages(foundAuctionImages);
              setIsAuctionModalOpen(true);
            }
          } catch (err) {
            console.error('Failed to fetch cert images:', err);
            alert('Failed to fetch images from certification service. Please try again.');
          } finally {
            setIsFetchingImages(false);
          }
        }}
      />

      <div className="bg-white shadow overflow-hidden sm:rounded-lg mt-4">
        <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-1 gap-0">
          {/* Image section */}
          <div className="p-4 bg-gray-50">
            <ImageManager
              coinId={coin.id}
              coinImages={coin.images || []}
              onImagesChange={(newImages) => {
                setCoin(prev => ({
                  ...prev,
                  images: newImages
                }));
              }}
              onImageClick={(index) => handleImageClick(coin.images || [], index)}
              fileInputRef={fileInputRef}
              setIsUploading={setIsUploading}
            />
          </div>

          {/* Details section */}
          <div>
            <BasicInfo coin={coin} />

            <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-8">
                <CoinDetails coin={coin} />
                <PurchaseInfo 
                  key={`purchase-info-${purchaseTransaction?.id || 'none'}-${coin?.purchase_date || 'none'}`}
                  coin={coin} 
                  purchaseTransaction={purchaseTransaction}
                  formatDate={(date, options) => formatDate(date, { ...options, user })}
                />
                <PopulationInfo coin={coin} />
                <GradingHistory 
                  submissionHistory={submissionHistory}
                  loadingSubmissions={loadingSubmissions}
                  formatDate={(date, options) => formatDate(date, { ...options, user })}
                />
                <DocumentsSection
                  documents={coin.documents}
                  coinId={coin.id}
                  onUploadSuccess={(newDocument) => {
                    setCoin(prevCoin => ({
                      ...prevCoin,
                      documents: [...(prevCoin.documents || []), newDocument]
                    }));
                  }}
                />
                <CustomFieldsSection 
                  coinId={coin.id} 
                  refreshTrigger={coin.updated_at} 
                />
                <Notes notes={coin.notes} />
                <Provenance provenance={coin.provenance} />
                <CoinDetailsText text={coin.coin_details} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <SaleTransactionModal
        isOpen={isTransactionModalOpen}
        onClose={() => setIsTransactionModalOpen(false)}
        onSubmit={async (transactionData) => {
          try {
            await transactions.create(transactionData);
            // Refresh coin data to show updated status
            const updatedCoin = await coins.getById(id);
            setCoin(updatedCoin);
            setIsTransactionModalOpen(false);
          } catch (err) {
            console.error('Failed to create transaction:', err);
            setError('Failed to record sale. Please try again.');
          }
        }}
        coins={[coin]}
      />

      {selectedImage && (
        <ImageViewerModal
          selectedImage={selectedImage}
          onClose={closeImageModal}
          onNavigate={handleNavigate}
        />
      )}

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        title="Delete Coin"
        message="Are you sure you want to delete this coin? This action cannot be undone."
        onConfirm={handleConfirmDelete}
        onCancel={() => setIsDeleteModalOpen(false)}
      />

      <AuctionImagesModal
        isOpen={isAuctionModalOpen}
        onClose={() => {
          setIsAuctionModalOpen(false);
          setAuctionImages([]);
        }}
        onConfirm={handleAuctionImagesConfirm}
        imageCount={auctionImages.length}
      />
    </div>
  );
};

export default ViewCoin;
